// Adaptive page wrapper. In Shopify embed renders the Polaris Page
// (gives the admin chrome, breadcrumbs, etc). In public web renders a
// senior-friendly consumer page with a large title and an optional
// back button. The page content stays identical so each page can stay
// a single component.

import { Page } from '@shopify/polaris';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsEmbedded } from '../hooks/useIsEmbedded';

interface AppPageProps {
  title: string;
  back?: { to: string; label?: string };
  children: ReactNode;
}

export function AppPage({ title, back, children }: AppPageProps): JSX.Element {
  const isEmbedded = useIsEmbedded();
  const navigate = useNavigate();

  if (isEmbedded) {
    return (
      <Page
        title={title}
        backAction={back ? { onAction: () => navigate(back.to), content: back.label } : undefined}
      >
        {children}
      </Page>
    );
  }

  return (
    <div className="consumer-page">
      <div className="consumer-page__header">
        {back ? (
          <button
            type="button"
            className="consumer-page__back"
            onClick={() => navigate(back.to)}
            aria-label={back.label ?? 'Back'}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M12 4l-6 6 6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{back.label ?? 'Back'}</span>
          </button>
        ) : null}
        <h1 className="consumer-page__title">{title}</h1>
      </div>
      <div className="consumer-page__body">{children}</div>
    </div>
  );
}
